import { faMagnifyingGlass, faXmark } from '@fortawesome/pro-solid-svg-icons';
import { FC, useEffect, useRef, useState } from 'react';
import FavoritesButton from '@/components/common/layout/header-toolbar/favorites-button';
import MyCartButton from '@/components/common/layout/header-toolbar/my-cart-button';
import UserAuthToolbarSlot from '@/components/common/layout/user-button/user-auth-toolbar-slot';
import { Icon, IconButton, Link } from '@/components/ui';
import { RenderableImage } from '@/components/ui/image/renderable-image';
import useCoreConfig from '@/hooks/use-core-config';
import useHeaderContent from '@/hooks/use-header-content';
import { useResolveThemeColor } from '@/hooks/use-resolve-theme-color';
import { useScrollYForHeaderLayout } from '@/hooks/use-scroll-y-for-header-layout';
import { useAppContext } from '@/providers/app-provider/app-provider';
import { useKeycloak } from '@/providers/keycloak-provider';
import { cn } from '@/utils';
import FeatureFlag from '../../feature-flag/feature-flag';
import ContactBanner from '../contact-banner/contact-banner';
import LanguageSelector from './language-selector/language-selector';
import MobileMenu from './mobile-menu/mobile-menu';
import NavigationBar from './navigation-bar/navigation-bar';
import SearchInput from './search-input/search-input';

interface BlackAppBarProps {
  isLandingPage?: boolean;
}

const SecondaryAppBar: FC<BlackAppBarProps> = () => {
  const resolveThemeColor = useResolveThemeColor();
  const { isStudioPreview } = useAppContext<{ isStudioPreview?: boolean }>();
  const stickyRef = useRef<HTMLDivElement>(null);
  const { isAuthenticated } = useKeycloak();
  const { features } = useCoreConfig();
  const scrollY = useScrollYForHeaderLayout(isStudioPreview, stickyRef);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const { logoSrc } = useCoreConfig();

  // Defaults to `true` so SSR / first hydration render matches the closed
  // modal state (see `age-verification-modal.tsx`); the real value is read
  // in the effect below. Touching `sessionStorage` synchronously during
  // render would crash on the server.
  const [isAgeVerified, setIsAgeVerified] = useState(true);

  useEffect(() => {
    setIsAgeVerified(sessionStorage.getItem('isAgeVerified') !== null);
  }, []);

  const headerContent = useHeaderContent();
  const parsedLogoHeight = Number(headerContent.logoHeight);
  const logoHeight =
    Number.isFinite(parsedLogoHeight) && parsedLogoHeight > 0 ? parsedLogoHeight : 40;
  const resolvedLogoSrc = headerContent.logoSrc || logoSrc || '/imgs/ibee-logo.png';

  const searchChromeColor = resolveThemeColor(headerContent.mainRowTextColor ?? 'foreground');

  return (
    <div
      ref={stickyRef}
      className={cn(
        'sticky top-0 left-0 w-full transition-shadow duration-300',
        scrollY > 30 ? 'shadow-md' : 'shadow-none',
        isAgeVerified ? 'z-50' : 'z-40',
      )}
    >
      <div className="flex flex-col">
        {headerContent.contactBanner && <ContactBanner />}
        <div style={{ background: resolveThemeColor(headerContent.background ?? 'foreground') }}>
          <div className="container mx-auto flex w-full items-center p-4 @xl:px-5">
            <div className="flex shrink-0 items-center gap-4">
              <div
                className={cn(
                  'transform-origin-left transition-transform duration-300',
                  scrollY > 30 ? 'scale-85' : 'scale-100',
                )}
              >
                <Link href="/">
                  <div className="flex items-center" style={{ height: `${logoHeight}px` }}>
                    <RenderableImage
                      src={resolvedLogoSrc}
                      height={logoHeight}
                      className="block h-full w-auto max-w-[220px] cursor-pointer object-contain"
                      alt="logo"
                    />
                  </div>
                </Link>
              </div>
            </div>
            <div
              className={cn(
                'mx-auto hidden overflow-hidden transition-all duration-300 ease-in-out @xl:flex',
                isSearchOpen ? 'max-w-0 opacity-0' : 'max-w-full opacity-100',
              )}
              aria-hidden={isSearchOpen}
            >
              <div className="flex items-center">
                <NavigationBar />
              </div>
            </div>
            <div className="ml-auto flex shrink-0 items-center gap-0 @sm:gap-0.5 @xl:ml-0 @xl:gap-0.5">
              <FeatureFlag feature="globalSearch">
                <div className="relative hidden items-center @xl:flex">
                  <div
                    className={cn(
                      'overflow-hidden transition-all duration-300 ease-in-out',
                      isSearchOpen ? 'mr-2 w-[470px] opacity-100' : 'w-0 opacity-0',
                    )}
                  >
                    <SearchInput
                      ref={searchInputRef}
                      isVisible={isSearchOpen}
                      setIsSearchOpen={setIsSearchOpen}
                    />
                  </div>
                  <IconButton
                    aria-label="Search"
                    variant="light"
                    size="lg"
                    onPress={() => {
                      setIsSearchOpen(true);
                      setTimeout(() => searchInputRef.current?.focus(), 350);
                    }}
                    className={cn('px-8', isSearchOpen && 'hidden')}
                  >
                    <Icon
                      icon={faMagnifyingGlass}
                      style={{ color: searchChromeColor }}
                      fontSize={32}
                    />
                  </IconButton>
                  <IconButton
                    aria-label="Close search"
                    variant="light"
                    size="lg"
                    onPress={() => {
                      searchInputRef.current?.blur();
                      setIsSearchOpen(false);
                    }}
                    className={cn('px-8', !isSearchOpen && 'hidden')}
                  >
                    <Icon icon={faXmark} style={{ color: searchChromeColor }} fontSize={32} />
                  </IconButton>
                </div>
              </FeatureFlag>

              <FeatureFlag feature="favorites">
                <FavoritesButton variant="secondary" />
              </FeatureFlag>

              {(isAuthenticated || features.cartForGuests) && <MyCartButton variant="secondary" />}

              <UserAuthToolbarSlot variant="secondary" />
              <div className="ml-4 hidden @xl:block">
                <LanguageSelector />
              </div>
              <MobileMenu logoSrc={resolvedLogoSrc} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SecondaryAppBar;
